import client from './index'
import { ApiResponse } from '@/models/api'

// Golf Car Profiles
export interface GolfCarProfile {
  id: string
  name: string
  model_name: string
  seats: number
  description?: string
  geo_zone_map_url?: string
  status: 'active' | 'inactive' | 'admin_blocked'
  admin_comment?: string
  assets_count?: number
  images?: GolfCarImage[]
  assets?: GolfCarAsset[]
  trips?: GolfTrip[]
  created_at: string
  updated_at: string
}

export interface CreateGolfCarProfilePayload {
  name: string
  model_name: string
  seats?: number
  description?: string
  geo_zone_map_url?: string
  tariff_ids?: string[]
}

export interface UpdateGolfCarProfilePayload {
  name?: string
  model_name?: string
  seats?: number
  description?: string
  geo_zone_map_url?: string
  status?: 'active' | 'inactive' | 'admin_blocked'
  tariff_ids?: string[]
}

// Golf Car Assets
export interface GolfCarAsset {
  id: string
  golf_car_profile_id: string
  asset_number?: string
  stall_number?: string
  battery_pct?: number
  current_range_km?: number
  lat?: number
  lng?: number
  wialon_unit_id?: string
  wialon_unit_name?: string
  last_telemetry_at?: string
  status?: string
  zone_id?: string
  created_at: string
  updated_at: string
}

export interface CreateGolfCarAssetPayload {
  asset_number?: string
  stall_number?: string
  wialon_unit_id?: string
  wialon_unit_name?: string
  status?: string
  zone_id?: string
}

export interface UpdateGolfCarAssetPayload {
  asset_number?: string
  stall_number?: string
  wialon_unit_id?: string
  wialon_unit_name?: string
  status?: string
  zone_id?: string
}

export interface BlockGolfCarAssetPayload {
  comment: string
}

// Golf Car Images
export interface GolfCarImage {
  id: string
  golf_car_profile_id: string
  s3_key: string
  secure_url: string
  type?: 'front' | 'back' | 'left' | 'right'
  sort_order?: number
  created_at: string
}

export interface CreateGolfCarImagePayload {
  s3_key: string
  secure_url: string
  sort_order?: number
}

// Golf Tariffs
export interface GolfTariff {
  id: string
  name: string
  pricing_type: 'hourly' | 'daily' | 'per_minute'
  base_hourly_rate?: number
  subsequent_hour_discount?: number
  available_durations?: number[]
  per_minute_rate?: number
  workday_rate?: number
  weekend_rate?: number
  deposit: number
  is_active: boolean
  tier1_min_days?: number
  tier1_max_days?: number
  tier1_discount_pct?: number
  tier2_min_days?: number
  tier2_max_days?: number
  tier2_discount_pct?: number
  tier3_min_days?: number
  tier3_max_days?: number
  tier3_discount_pct?: number
  tier4_min_days?: number
  tier4_max_days?: number
  tier4_discount_pct?: number
  created_at: string
  updated_at: string
}

export interface CreateGolfTariffPayload {
  name: string
  pricing_type: 'hourly' | 'daily' | 'per_minute'
  base_hourly_rate?: number
  subsequent_hour_discount?: number
  available_durations?: number[]
  per_minute_rate?: number
  workday_rate?: number
  weekend_rate?: number
  tier1_min_days?: number
  tier1_max_days?: number
  tier1_discount_pct?: number
  tier2_min_days?: number
  tier2_max_days?: number
  tier2_discount_pct?: number
  tier3_min_days?: number
  tier3_max_days?: number
  tier3_discount_pct?: number
  tier4_min_days?: number
  tier4_max_days?: number
  tier4_discount_pct?: number
  deposit: number
  is_active?: boolean
}

// Golf Trips
export interface GolfTrip {
  id: string
  golf_car_profile_id: string
  asset_id?: string
  tariff_id?: string
  user_id: string
  status:
    | 'pending'
    | 'approved'
    | 'ongoing'
    | 'on_hold'
    | 'completed'
    | 'cancelled_by_user'
    | 'cancelled_by_admin'
    | 'cancelled_by_system'
  pricing_type: 'hourly' | 'daily' | 'per_minute'
  payment_method: 'card' | 'cash'
  payment_status?: 'pending' | 'frozen' | 'charged' | 'refunded' | 'failed'
  duration_minutes?: number
  reservation_start?: string
  reservation_end?: string
  trip_start_at?: string
  trip_end_at?: string
  assigned_stall_number?: string
  base_rate_amount?: number
  deposit_amount?: number
  discount_amount?: number
  total_amount: number
  promo_code?: string
  pretrip_photos?: Array<{
    s3_key: string
    url: string
    angle: 'front' | 'back' | 'left' | 'right'
  }>
  aftertrip_photos?: Array<{
    s3_key: string
    url: string
    angle: 'front' | 'back' | 'left' | 'right'
  }>
  extend_history?: Array<{
    type: 'hourly' | 'daily'
    duration_minutes?: number
    added_at: string
  }>
  approved_by_admin_id?: string
  approved_at?: string
  admin_comment?: string
  start_date?: string
  end_date?: string
  created_at: string
  updated_at: string
  renter?: GolfUser
  golf_car_profile?: GolfCarProfile
  profile?: GolfCarProfile
  asset?: GolfCarAsset
  tariff?: GolfTariff
  logs?: GolfTripLog[]
  payment?: GolfTripPayment
}

export interface GolfTripLog {
  id: string
  golf_trip_id: string
  user_id?: string
  admin_id?: string
  action:
    | 'booking_created'
    | 'booking_approved'
    | 'booking_declined'
    | 'trip_started'
    | 'pretrip_photos_uploaded'
    | 'trip_extended'
    | 'trip_ended'
    | 'aftertrip_photos_uploaded'
    | 'trip_completed'
    | 'cancelled_by_user'
    | 'cancelled_by_admin'
    | 'payment_frozen'
    | 'payment_charged'
    | 'payment_unfrozen'
    | 'asset_assigned'
    | 'asset_changed'
    | 'admin_comment'
    | 'discount_added'
    | 'discount_removed'
    | 'trip_on_hold'
    | 'trip_unhold'
    | 'telemetry_synced'
  comment?: string
  created_at: string
}

export interface GolfTripPayment {
  id: string
  golf_trip_id: string
  freeze_order_id?: string
  freeze_amount?: number
  charge_amount?: number
  refund_amount?: number
  deposit_frozen_amount?: number
  deposit_charged_amount?: number
  deposit_refunded_amount?: number
  service_frozen_amount?: number
  service_charged_amount?: number
  service_refunded_amount?: number
  payment_method?: string
  payment_status: 'pending' | 'frozen' | 'charged' | 'refunded' | 'failed'
  is_cash: boolean
  created_at: string
  updated_at: string
}

// Golf Users
export interface GolfUserTripSummary {
  id: string
  status: GolfTrip['status']
  pricing_type?: GolfTrip['pricing_type']
  base_rate_amount?: number
  total_amount?: number
  created_at?: string
}

export interface GolfUserReserveSummary {
  id: string
  status: GolfTrip['status']
  pricing_type?: GolfTrip['pricing_type']
  reserved_from?: string
  reserved_to?: string
  total_amount?: number
  created_at?: string
}

export interface GolfUserTransactionSummary {
  id: string
  amount: number
  action: GolfTransaction['action']
  type: GolfTransaction['type']
  description?: string | null
  created_at: string
}

export interface GolfUserPaymentCard {
  id: string
  user_id: string
  brand: string
  masked_pan: string
  card_uid?: string
  default: boolean
  created_at?: string
  updated_at?: string
}

export interface GolfUser {
  id: string
  name: string
  email: string
  status?: string
  phone?: string
  age?: number
  driving_experience_months?: number
  status_golf: 'active' | 'inactive'
  trips_count: number
  last_trip_date?: string
  image?: string | null
  driver_license?: string | null
  driver_license_backside?: string | null
  passport_or_id?: string | null
  passport_or_id_backside?: string | null
  user_docs_selfie?: string | null
  first_name?: string | null
  last_name?: string | null
  date_of_birth?: string | null
  country?: string | null
  city?: string | null
  driver_license_issue_date?: string | null
  payment_cards?: GolfUserPaymentCard[]
  golf?: {
    status_golf: 'active' | 'inactive'
    trips_total?: number
    trips_completed?: number
    recent_trips?: GolfUserTripSummary[]
    recent_reserves?: GolfUserReserveSummary[]
    recent_transactions?: GolfUserTransactionSummary[]
  }
}

// Golf Settings
export interface GolfSettings {
  access: {
    min_age: number
    min_driving_experience_months: number
  }
  general: {
    support_phone: string
    pending_booking_expire_minutes: number
    availability_window_hours?: number
  }
}

// Geographic Areas
export interface GolfCarArea {
  id: string
  name: string
  type: 'parking' | 'alert'
  points: Array<{ lat: number; lng: number }>
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface CreateGolfCarAreaPayload {
  name: string
  type: 'parking' | 'alert'
  points: Array<{ lat: number; lng: number }>
  is_active?: boolean
}

export interface UpdateGolfCarAreaPayload {
  name?: string
  type?: 'parking' | 'alert'
  points?: Array<{ lat: number; lng: number }>
  is_active?: boolean
}

// Dashboard
export interface GolfDashboard {
  total_profiles: number
  total_trips: number
  ongoing_trips: number
  pending_trips: number
  completed_trips: number
  cancelled_trips: number
  unique_renters: number
  total_revenue: number
}

// Telemetry
export interface GolfAssetTelemetry {
  id: string
  golf_car_asset_id: string
  battery_pct?: number
  current_range_km?: number
  lat?: number
  lng?: number
  last_telemetry_at?: string
  wialon_unit_id?: string
}

// Promo Codes
export interface GolfPromoCode {
  id: string
  code: string
  campaign_name?: string
  type: 'percent' | 'fixed' | 'wallet_credit' | 'free_minutes'
  discount_type?: 'percentage' | 'fixed'
  discount_value: number | string
  min_amount?: number | string
  max_discount?: number | string
  allowed_modules?: string[]
  status: 'draft' | 'active' | 'paused' | 'expired' | 'stopped'
  usage_limit?: number
  used_count: number
  budget?: number | string
  spent_budget?: number | string
  new_users_only?: boolean
  valid_from?: string
  valid_until?: string
  is_active?: boolean
  applicable_tariff_ids: string[] | Record<string, unknown> | null
  description?: string
  created_at: string
  updated_at: string
}

export interface CreateGolfPromoCodePayload {
  code?: string
  campaign_name?: string
  type?: 'percent' | 'fixed' | 'wallet_credit' | 'free_minutes'
  discount_type?: 'percentage' | 'fixed'
  discount_value: number
  min_amount?: number
  max_discount?: number
  allowed_modules?: string[]
  usage_limit?: number
  budget?: number
  new_users_only?: boolean
  valid_from?: string
  valid_until?: string
  is_active?: boolean
  status?: 'draft' | 'active' | 'paused' | 'stopped'
  applicable_tariff_ids?: string[]
  description?: string
}

export interface BulkCreateGolfPromoCodePayload {
  count: number
  prefix?: string
  campaign_name?: string
  type?: 'percent' | 'fixed' | 'wallet_credit' | 'free_minutes'
  discount_value: number
  max_discount?: number
  min_amount?: number
  allowed_modules?: string[]
  usage_limit?: number
  budget?: number
  new_users_only?: boolean
  valid_from?: string
  valid_until?: string
  description?: string
}

export interface PromoCodeStats {
  totalCampaigns: number
  activeCampaigns: number
  totalRedemptions: number
  totalSpentBudget: number
}

export interface UpdateGolfPromoCodePayload {
  campaign_name?: string
  code?: string
  type?: 'percent' | 'fixed' | 'wallet_credit' | 'free_minutes'
  discount_type?: 'percentage' | 'fixed'
  discount_value?: number
  min_amount?: number
  max_discount?: number
  usage_limit?: number
  budget?: number
  new_users_only?: boolean
  valid_from?: string
  valid_until?: string
  description?: string
  status?: 'draft' | 'active' | 'paused' | 'expired' | 'stopped'
  is_active?: boolean
  allowed_modules?: string[]
  applicable_tariff_ids?: string[]
}

export interface SyncTelemetryPayload {
  battery_pct?: number
  current_range_km?: number
  lat?: number
  lng?: number
}

// ============ PROFILES ============

export const getGolfCarProfiles = (filters?: { status?: string }) => {
  return client.get<ApiResponse<GolfCarProfile[]>>('/golf/profiles', {
    params: filters,
  })
}

export const getGolfCarProfile = (id: string) => {
  return client.get<ApiResponse<GolfCarProfile>>(`/golf/profiles/${id}`)
}

export const getGolfCarProfileTrips = (id: string) => {
  return client.get<ApiResponse<GolfTrip[]>>(`/golf/profiles/${id}/trips`)
}

export const createGolfCarProfile = (payload: CreateGolfCarProfilePayload) => {
  return client.post<ApiResponse<GolfCarProfile>>('/golf/profiles', payload)
}

export const updateGolfCarProfile = (
  id: string,
  payload: UpdateGolfCarProfilePayload
) => {
  return client.put<ApiResponse<GolfCarProfile>>(
    `/golf/profiles/${id}`,
    payload
  )
}

export const blockGolfCarProfile = (id: string, comment: string) => {
  return client.post<ApiResponse<GolfCarProfile>>(
    `/golf/profiles/${id}/block`,
    { comment }
  )
}

export const unblockGolfCarProfile = (id: string, comment?: string) => {
  return client.post<ApiResponse<GolfCarProfile>>(
    `/golf/profiles/${id}/unblock`,
    { comment }
  )
}

// ============ ASSETS ============

export const createGolfCarAsset = (
  profileId: string,
  payload: CreateGolfCarAssetPayload
) => {
  return client.post<ApiResponse<GolfCarAsset>>(
    `/golf/profiles/${profileId}/assets`,
    payload
  )
}

export const updateGolfCarAsset = (
  id: string,
  payload: UpdateGolfCarAssetPayload
) => {
  return client.put<ApiResponse<GolfCarAsset>>(`/golf/assets/${id}`, payload)
}

export const blockGolfCarAsset = (
  id: string,
  payload: BlockGolfCarAssetPayload
) => {
  return client.post<ApiResponse<GolfCarAsset>>(
    `/golf/assets/${id}/block`,
    payload
  )
}

export const unblockGolfCarAsset = (id: string, comment?: string) => {
  return client.post<ApiResponse<GolfCarAsset>>(`/golf/assets/${id}/unblock`, {
    comment,
  })
}

// ============ IMAGES ============

export const createGolfCarImage = (profileId: string, formData: FormData) => {
  return client.post<ApiResponse<GolfCarImage>>(
    `/golf/profiles/${profileId}/images`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  )
}

export const deleteGolfCarImage = (imageId: string) => {
  return client.delete<ApiResponse<void>>(`/golf/images/${imageId}`)
}

// ============ TARIFFS ============

export const getGolfTariffs = () => {
  return client.get<ApiResponse<GolfTariff[]>>('/golf/tariffs')
}

export const createGolfTariff = (payload: CreateGolfTariffPayload) => {
  return client.post<ApiResponse<GolfTariff>>('/golf/tariffs', payload)
}

export const updateGolfTariff = (
  id: string,
  payload: Partial<CreateGolfTariffPayload>
) => {
  return client.put<ApiResponse<GolfTariff>>(`/golf/tariffs/${id}`, payload)
}

// ============ TRIPS ============

export const getGolfTrips = (filters?: {
  status?: string
  pricing_type?: string
  payment_method?: string
  user_id?: string
  page?: number
  per_page?: number
}) => {
  return client.get<ApiResponse<GolfTrip[]>>('/golf/trips', { params: filters })
}

export const getGolfTrip = (id: string) => {
  return client.get<ApiResponse<GolfTrip>>(`/golf/trips/${id}`)
}

export const approveGolfTrip = (id: string) => {
  return client.post<ApiResponse<GolfTrip>>(`/golf/trips/${id}/approve`)
}

export const declineGolfTrip = (id: string, comment?: string) => {
  return client.post<ApiResponse<GolfTrip>>(`/golf/trips/${id}/decline`, {
    comment,
  })
}

export const completeGolfTrip = (id: string) => {
  return client.post<ApiResponse<GolfTrip>>(`/golf/trips/${id}/complete`)
}

export const endGolfTrip = (id: string) => {
  return client.post<ApiResponse<GolfTrip>>(`/golf/trips/${id}/end`)
}

export const cancelGolfTrip = (id: string, comment?: string) => {
  return client.post<ApiResponse<GolfTrip>>(`/golf/trips/${id}/cancel`, {
    comment,
  })
}

export const holdGolfTrip = (id: string, reason?: string) => {
  return client.post<ApiResponse<GolfTrip>>(`/golf/trips/${id}/hold`, {
    reason,
  })
}

export const unholdGolfTrip = (id: string) => {
  return client.post<ApiResponse<GolfTrip>>(`/golf/trips/${id}/unhold`)
}

export const discountGolfTrip = (
  id: string,
  amount: number,
  reason?: string
) => {
  return client.post<ApiResponse<GolfTrip>>(`/golf/trips/${id}/discount`, {
    amount,
    reason,
  })
}

export interface GolfTripDiscount {
  id: string
  amount: number
  reason?: string
  admin_id?: string
  applied_at: string
}

export const getGolfTripDiscounts = (id: string) => {
  return client.get<ApiResponse<GolfTripDiscount[]>>(
    `/golf/trips/${id}/discounts`
  )
}

export const removeGolfTripDiscount = (tripId: string, discountId: string) => {
  return client.delete<ApiResponse<void>>(`/golf/trips/${tripId}/discount`, {
    data: { discount_id: discountId },
  })
}

export const changeTripAsset = (id: string, assetId: string) => {
  return client.put<ApiResponse<GolfTrip>>(`/golf/trips/${id}/asset`, {
    asset_id: assetId,
  })
}

export interface FinishGolfPaymentPayload {
  transaction_type: 'deposit' | 'service'
  action: 'freeze' | 'charge' | 'unfreeze' | 'refund' | 'collect' | 'return'
  amount: number
}

export interface GolfTransaction {
  id: string
  golf_trip_id: string
  user_id: string
  payment_card_id?: string
  admin_id?: string
  order_id?: string
  amount: number
  action: 'freeze' | 'charge' | 'unfreeze' | 'refund'
  type: 'deposit' | 'service'
  description?: string
  created_at: string
}

export const finishGolfPayment = (
  id: string,
  payload: FinishGolfPaymentPayload
) => {
  return client.post<
    ApiResponse<{ message: string; amount: number; transaction_type: string }>
  >(`/golf/trips/${id}/finish-payment`, payload)
}

export const getGolfTransactions = (params?: {
  trip_id?: string
  user_id?: string
  action?: string
  type?: string
  page?: number
  per_page?: number
}) => {
  return client.get<ApiResponse<{ data: GolfTransaction[]; meta: any }>>(
    '/golf/transactions',
    { params }
  )
}

// ============ USERS ============

export const getGolfUsers = (filters?: {
  status_golf?: string
  q?: string
  page?: number
  per_page?: number
}) => {
  return client.get<ApiResponse<GolfUser[]>>('/users/golf', { params: filters })
}

export const getGolfUser = (id: string) => {
  return client.get<ApiResponse<GolfUser>>(`/users/golf/${id}`)
}

export const updateGolfUserStatus = (
  id: string,
  statusGolf: 'active' | 'inactive'
) => {
  return client.put<ApiResponse<GolfUser>>(`/users/golf/${id}/status`, {
    status_golf: statusGolf,
  })
}

// ============ SETTINGS ============

export const getGolfSettings = () => {
  return client.get<ApiResponse<GolfSettings>>('/golf/settings')
}

export interface UpdateGolfSettingPayload {
  group: string
  key: string
  value: string | number
}

export const updateGolfSetting = (payload: UpdateGolfSettingPayload) => {
  return client.put<ApiResponse<GolfSettings>>('/golf/settings', payload)
}

export const updateGolfSettingsBulk = (payload: Record<string, any>) => {
  return client.put<ApiResponse<GolfSettings>>('/golf/settings/bulk', payload)
}

// ============ AREAS ============

export const getGolfAreas = () => {
  return client.get<ApiResponse<GolfCarArea[]>>('/golf/areas')
}

export const createGolfArea = (payload: CreateGolfCarAreaPayload) => {
  return client.post<ApiResponse<GolfCarArea>>('/golf/areas', payload)
}

export const updateGolfArea = (
  id: string,
  payload: UpdateGolfCarAreaPayload
) => {
  return client.put<ApiResponse<GolfCarArea>>(`/golf/areas/${id}`, payload)
}

export const deleteGolfArea = (id: string) => {
  return client.delete<ApiResponse<void>>(`/golf/areas/${id}`)
}

// ============ TELEMETRY ============

export const getAssetTelemetry = (assetId: string) => {
  return client.get<ApiResponse<GolfAssetTelemetry>>(
    `/golf/assets/${assetId}/telemetry`
  )
}

export const syncAssetTelemetry = (
  assetId: string,
  payload: SyncTelemetryPayload
) => {
  return client.post<ApiResponse<GolfAssetTelemetry>>(
    `/golf/assets/${assetId}/telemetry/sync`,
    payload
  )
}

// ============ DASHBOARD ============

export const getGolfDashboard = (params?: {
  date_from?: string
  date_to?: string
}) => {
  return client.get<ApiResponse<GolfDashboard>>('/golf/dashboard', { params })
}

// ============ RESERVES ============

export const getGolfReserves = (params?: {
  status?: string
  pricing_type?: string
  payment_method?: string
  profile_id?: string
  asset_id?: string
  user_id?: string
}) => {
  return client.get<ApiResponse<GolfTrip[]>>('/golf/reserves', { params })
}

export const getGolfReservesByProfile = (
  profileId: string,
  params?: {
    status?: string
    pricing_type?: string
    payment_method?: string
    asset_id?: string
  }
) => {
  return client.get<ApiResponse<GolfTrip[]>>(
    `/golf/reserves/profiles/${profileId}`,
    {
      params,
    }
  )
}

// ============ FINANCIAL REPORT ============

export interface GolfFinancialReportParams {
  date_from?: string
  date_to?: string
  format?: 'json' | 'xlsx'
}

export interface GolfFinancialReportSummary {
  totalTrips: number
  totalRevenue: number
  totalBaseRate: number
  totalDeposits: number
  totalDiscounts: number
  cardRevenue: number
  cashRevenue: number
  hourlyRevenue: number
  dailyRevenue: number
  perMinuteRevenue: number
}

export interface GolfFinancialReportDaily {
  date: string
  tripsCount: number
  revenue: number
  cardRevenue: number
  cashRevenue: number
}

export interface GolfFinancialReportData {
  summary: GolfFinancialReportSummary
  dailyBreakdown: GolfFinancialReportDaily[]
  trips: GolfTrip[]
  meta?: {
    range?: {
      date_from?: string
      date_to?: string
    }
  }
}

export const getGolfFinancialReport = (params?: GolfFinancialReportParams) => {
  return client.get<ApiResponse<GolfFinancialReportData>>(
    '/golf/financial-report',
    {
      params,
      responseType: params?.format === 'xlsx' ? 'blob' : 'json',
    }
  )
}

// ============ PROMO CODES ============

export const getGolfPromoCodes = () => {
  return client.get<ApiResponse<{ data: GolfPromoCode[] }>>('/golf/promo-codes')
}

export const getGolfPromoCode = (id: string) => {
  return client.get<ApiResponse<GolfPromoCode>>(`/golf/promo-codes/${id}`)
}

export const createGolfPromoCode = (payload: CreateGolfPromoCodePayload) => {
  return client.post<ApiResponse<GolfPromoCode>>('/golf/promo-codes', payload)
}

export const updateGolfPromoCode = (
  id: string,
  payload: UpdateGolfPromoCodePayload
) => {
  return client.put<ApiResponse<{ data: { data: GolfPromoCode } }>>(
    `/golf/promo-codes/${id}`,
    payload
  )
}

export const deleteGolfPromoCode = (id: string) => {
  return client.delete<ApiResponse<void>>(`/golf/promo-codes/${id}`)
}

export const deactivateGolfPromoCode = (id: string) => {
  return client.post<ApiResponse<{ message: string }>>(
    `/golf/promo-codes/${id}/deactivate`
  )
}

export const bulkCreateGolfPromoCodes = (
  payload: BulkCreateGolfPromoCodePayload
) => {
  return client.post<ApiResponse<{ count: number; codes: string[] }>>(
    '/golf/promo-codes/bulk',
    payload
  )
}

export const pauseGolfPromoCode = (id: string) => {
  return client.post<ApiResponse<GolfPromoCode>>(
    `/golf/promo-codes/${id}/pause`
  )
}

export const resumeGolfPromoCode = (id: string) => {
  return client.post<ApiResponse<GolfPromoCode>>(
    `/golf/promo-codes/${id}/resume`
  )
}

export const stopGolfPromoCode = (id: string) => {
  return client.post<ApiResponse<GolfPromoCode>>(`/golf/promo-codes/${id}/stop`)
}

export const exportGolfPromoCodesCsv = () => {
  return client.get('/golf/promo-codes/export/csv', {
    responseType: 'blob',
  })
}

export const getGolfPromoCodeStats = () => {
  return client.get<ApiResponse<PromoCodeStats>>(
    '/golf/promo-codes/overview/stats'
  )
}

// Wialon Units
export interface WialonUnit {
  id: number
  name: string
  lat: number | null
  lng: number | null
  speed: number
  last_update: string | null
  is_linked: boolean
}

export const getWialonUnits = () => {
  return client.get<ApiResponse<WialonUnit[]>>('/golf/wialon-units')
}
